import { useState } from "react";
import { toast } from "sonner";
import { Copy, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Field, inputCls, btnPrimary, btnGhost } from "@/components/Form";

const DAY_PRESETS = [30, 60, 90];

function ActionDialog({ open, onClose, title, desc, children }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="client-action-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>{desc}</DialogDescription></DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}

export function GrantDialog({ client, onClose, onDone }) {
  const [days, setDays] = useState(30);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${client.user_id}/license`, { days: Number(days), note });
      toast.success(`Licença liberada até ${fmtDate(data.plan_expires_at)}`); onDone(); onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <ActionDialog open onClose={onClose} title="Liberar licença" desc={`${client.name} · ${client.email}. Os dias são somados à validade atual.`}>
      <form onSubmit={submit} className="space-y-4">
        <div className="flex gap-2">{DAY_PRESETS.map((d) => (
          <button type="button" key={d} data-testid={`grant-preset-${d}`} onClick={() => setDays(d)}
            className={`inline-flex h-11 flex-1 items-center justify-center rounded-lg border text-sm font-semibold transition-colors ${Number(days) === d ? "border-amber-500 bg-amber-50 text-amber-800" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"}`}>{d} dias</button>
        ))}</div>
        <Field label="Dias (personalizado)"><input data-testid="grant-days-input" type="number" min={1} max={3650} required className={inputCls} value={days} onChange={(e) => setDays(e.target.value)} /></Field>
        <Field label="Observação (opcional)"><input data-testid="grant-note-input" className={inputCls} placeholder="Ex.: pagou via Pix direto" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
        <button data-testid="grant-submit-btn" disabled={busy} className={`${btnPrimary} w-full`}>{busy && <Loader2 className="h-4 w-4 animate-spin" />}Liberar {days} dias</button>
      </form>
    </ActionDialog>
  );
}

export function CreditsDialog({ client, onClose, onDone }) {
  const [amount, setAmount] = useState(10);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${client.user_id}/credits`, { amount: Number(amount), note });
      toast.success(`Saldo de bônus: ${data.bonus_credits} contestações`); onDone(); onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <ActionDialog open onClose={onClose} title="Dar contestações grátis" desc={`${client.name} · saldo atual: ${client.bonus_credits || 0}. O bônus é usado quando o limite do plano acaba.`}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Quantidade"><input data-testid="credits-amount-input" type="number" min={1} max={1000} required className={inputCls} value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
        <Field label="Observação (opcional)"><input data-testid="credits-note-input" className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
        <button data-testid="credits-submit-btn" disabled={busy} className={`${btnPrimary} w-full`}>{busy && <Loader2 className="h-4 w-4 animate-spin" />}Adicionar +{amount}</button>
      </form>
    </ActionDialog>
  );
}

export function ResetPwDialog({ client, onClose, onDone }) {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/users/${client.user_id}/reset-password`); setResult(data); onDone(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const copy = () => navigator.clipboard.writeText(result.temp_password).then(() => toast.success("Senha copiada"));
  return (
    <ActionDialog open onClose={onClose} title="Resetar senha" desc={`${client.name} · ${client.email}`}>
      {!result ? (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">Uma senha provisória será gerada e enviada por e-mail. O cliente será desconectado e precisará criar uma nova senha no próximo login.</p>
          <button data-testid="reset-pw-confirm-btn" onClick={run} disabled={busy} className={`${btnPrimary} w-full`}>{busy && <Loader2 className="h-4 w-4 animate-spin" />}Gerar senha provisória</button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="reset-pw-result">
          <p className="text-sm text-slate-600">{result.emailed ? "Enviada para o e-mail do cliente." : "Não foi possível enviar o e-mail. Repasse a senha ao cliente."}</p>
          <div className="flex items-center gap-2 rounded-lg bg-slate-100 p-3">
            <code data-testid="reset-pw-temp" className="flex-1 font-mono text-lg font-bold tracking-widest">{result.temp_password}</code>
            <button data-testid="reset-pw-copy-btn" onClick={copy} className={btnGhost}><Copy className="h-4 w-4" /></button>
          </div>
        </div>
      )}
    </ActionDialog>
  );
}
